import {
  Box,
  Button,
  Dialog,
  DialogContent,
  Divider,
  List,
  ListItem,
  ListItemText,
  Snackbar,
  SnackbarContent,
  Typography
} from '@mui/material';
import React, { FC, memo, useMemo } from 'react';
import { actionBarHeight, appBarHeight } from 'src/constants/layout';
import { PlanningSuggestion } from 'src/entities/PlanningsSuggestions.entity';

type Props = {
  showNotAllocSuggestionSnackbar: boolean;
  suggestionDialogOpen: boolean;
  notAllocSuggestions: PlanningSuggestion[] | undefined;
  handleSuggestionDialogClose: (event: React.MouseEvent<HTMLElement>) => void;
  setShowNotAllocSuggestionSnackbar: (show: boolean) => void;
  setSuggestionDialogOpen: (open: boolean) => void;
};

const NotAllocSuggestionComponent: FC<Props> = memo(({
  showNotAllocSuggestionSnackbar,
  suggestionDialogOpen,
  notAllocSuggestions,
  handleSuggestionDialogClose,
  setShowNotAllocSuggestionSnackbar,
  setSuggestionDialogOpen,
}) => {
  const notAllocSuggestionSnackbarAction = useMemo(() => (
    <>
      <Button size="small" color="secondary" onClick={() => setSuggestionDialogOpen(true)}>
        確認する
      </Button>
      <Button size="small" color="secondary" onClick={() => setShowNotAllocSuggestionSnackbar(false)}>
        閉じる
      </Button>
    </>
  ), [setSuggestionDialogOpen, setShowNotAllocSuggestionSnackbar]);

  const notAllocSuggestionSnackbar = useMemo(() => (
    <Snackbar
      anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      open={showNotAllocSuggestionSnackbar && !suggestionDialogOpen}
      key="notAllocSuggestions"
      sx={{ mt: `${appBarHeight + actionBarHeight - 15}px`, }}
    >
      <SnackbarContent
        sx={{
          bgcolor: '#2472BD',
          '& .MuiSnackbarContent-message': {
            color: '#ffffff'
          },
          '& .MuiSnackbarContent-action': {
            '& .MuiButton-root': {
              color: '#ffffff'
            }
          }
        }}
        message="配車のヒントがあります。"
        action={notAllocSuggestionSnackbarAction}
      />
    </Snackbar>
  ), [notAllocSuggestionSnackbarAction, showNotAllocSuggestionSnackbar, suggestionDialogOpen]);

  const notAllocSuggestionDialog = useMemo(() => {
    if (!notAllocSuggestions) return null;

    return (
      <Dialog
        open={suggestionDialogOpen}
        onClose={handleSuggestionDialogClose}
        fullWidth
        maxWidth="sm"
      >
        <DialogContent sx={{ p: 2, }}>
          <Typography variant="h5" sx={{ mb: 0, fontWeight: 'bold' }}>
            配車のヒント
          </Typography>
          <Typography variant="body1" sx={{ mb: 2, }}>
            ※以下の項目は割り当て失敗の要因として可能性が高い順に表示しています。
          </Typography>
          {notAllocSuggestions.sort((a, b) => b.count - a.count).map((selectedSuggestion, index) => (
            <React.Fragment key={['selectedSuggestion', index].join('-')}>
              <Typography variant="body1" sx={{ mb: 0, fontWeight: 'bold', }}>
                {selectedSuggestion.reason}
              </Typography>
              {selectedSuggestion.actions.length > 0 && (
                <List sx={{ py: 0, m: 0, }}>
                  {selectedSuggestion.actions.map((action, idx) => (
                    <ListItem key={['selectedSuggestion', idx].join('-')} sx={{ pl: 0 }}>
                      <ListItemText
                        primary={action.suggestion}
                        secondary={action.explanation}
                        sx={{
                          '& .MuiListItemText-secondary': {
                            mt: 1,
                            color: 'text.primary'
                          }
                        }}
                      />
                    </ListItem>
                  ))}
                </List>
              )}
            </React.Fragment>
          ))}
        </DialogContent>
        <Divider />
        <Box sx={{ display: 'flex', justifyContent: 'flex-end', }}>
          <Button onClick={handleSuggestionDialogClose} color="primary">
            閉じる
          </Button>
        </Box>
      </Dialog>
    );
  }, [handleSuggestionDialogClose, notAllocSuggestions, suggestionDialogOpen]);

  return (
    <>
      {notAllocSuggestionSnackbar}
      {notAllocSuggestionDialog}
    </>
  );
});

export default NotAllocSuggestionComponent;
